import {
	Container,
	ContainerPadding,
	Text,
	TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { oneformBFFNdpsSend } from 'src/bff';
import {
	DetalleMotivo,
	MultipleCTASelection,
	MultipleOptionCTAs,
	SelectMarcaModelo,
} from 'src/components';
import {
	FormController,
	LayoutTemplate,
	useOneFormContext,
} from 'src/feature-app';
import { useTrackingManager } from 'src/feature-app/hooks/use-tracking-manager';
import { formDataBuilder } from 'src/forms/format-fields';
import { FormDataGroup, FormInfo, OneFormState, Steps } from 'src/types';
import {
	ConfirmacionNoRenovacion,
	FakeTriggerCTA,
	ThankYouNoRenovacion,
} from '.';
import {
	addRemovedStep,
	getIndexInSteps,
	removeStep,
	resetStepsScreenIndex,
} from '../helpers/add-remove-steps';
import {
	CTAsMotivoRenovador,
	CTAsMotivoRenovadorProblemas,
	valueMotivoOtros,
	valueMotivoProblemas,
} from './CtasNoRenovacion';
import { getCommonFormData } from './FormData';
import { NoRenovacionFormProps } from './NoRenovacionController';
import { useInitializeNoRenovacionForm } from './use-initialize-no-renovacion-form';

export function NoRenovacionYaTengo({
	dpsData,
	idVehicle,
	idPerson,
	handleError,
	trackFormLoad,
	estado,
}: NoRenovacionFormProps) {
	const { initializeForm } = useOneFormContext();
	const trackingManager = useTrackingManager();
	const store = useStore();
	const dispatch = useDispatch();
	const [presubmitSent, setPresubmitSent] = useState(false);
	const { formData } = useSelector((state: OneFormState) => state);
	const { selectMotivos, brand } = formData.fields;

	const vwBrandCode = '36';

	const CTAsUsadoNuevo = [
		{
			label: 'He comprado un vehículo nuevo',
			value: '1',
		},
		{
			label: 'He comprado un vehículo de ocasión',
			value: '2',
		},
	];

	const CTAsLugarCompra = [
		{
			label: 'Punto de venta oficial',
			value: '1',
		},
		{
			label: 'Punto de venta no oficial',
			value: '2',
		},
		{
			label: 'Empresa de Renting/Leasing',
			value: '3',
		},
		{
			label: 'Compra-venta privada',
			value: '4',
		},
	];

	let index = 0;
	const steps: Steps[] = [
		{
			title: (
				<>
					<Text bold>
						Nos encanta viajar contigo, pero sabemos que la vida da
						muchas vueltas y tenemos curiosidad…{' '}
					</Text>
					¿Qué modelo te ha conquistado?
				</>
			),
			fields: <LayoutTemplate element={<FakeTriggerCTA />} />,
			screenIndex: index++,
			name: 'introduccion',
		},
		{
			title: (
				<>
					<Text bold>
						¿Qué modelo{' '}
						<span style={{ whiteSpace: 'nowrap' }}>
							elegiste?
							<sup style={{ top: 'auto', fontSize: '100%' }}>
								*
							</sup>
						</span>
					</Text>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<SelectMarcaModelo
							stepName="Modelo"
							brandName="brand"
							modelName="modelo"
							required={true}
							formToken="VW_DDB_FORM_NORENUEVA"
						/>
					}
				/>
			),
			screenIndex: index++,
			name: 'Modelo',
			showOverflow: true,
		},
		{
			title: (
				<Text bold>
					¿Qué tipo de vehículo has{' '}
					<span style={{ whiteSpace: 'nowrap' }}>
						escogido?
						<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
					</span>
				</Text>
			),
			fields: (
				<LayoutTemplate
					element={
						<MultipleCTASelection
							ctaValues={CTAsUsadoNuevo}
							name="usadoNuevo"
							nextOnClick
						/>
					}
				/>
			),
			screenIndex: index++,
			name: 'usadoNuevo',
			ctas: CTAsUsadoNuevo,
		},
		{
			title: (
				<Text bold>
					¿Dónde has adquirido tu nuevo{' '}
					<span style={{ whiteSpace: 'nowrap' }}>
						vehículo?
						<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
					</span>
				</Text>
			),
			fields: (
				<LayoutTemplate
					element={
						<MultipleCTASelection
							ctaValues={CTAsLugarCompra}
							name="lugarCompra"
							nextOnClick
						/>
					}
				/>
			),
			screenIndex: index++,
			name: 'lugarCompra',
			ctas: CTAsLugarCompra,
		},
		{
			title: (
				<>
					<Text bold>¿Por qué motivos </Text>
					no has elegido un{' '}
					<span style={{ whiteSpace: 'nowrap' }}>
						Volkswagen?
						<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
					</span>
					<Container padding={{ top: ContainerPadding.dynamic0020 }}>
						<Text
							appearance={TokenTextAppearance.copy200}
							staticSize
						>
							Elige un máximo de 3 opciones
						</Text>
					</Container>
				</>
			),
			alternateTitle: (
				<>
					<Text bold>¿Por qué motivos </Text>
					no has elegido un{' '}
					<span style={{ whiteSpace: 'nowrap' }}>
						Volkswagen?
						<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
					</span>
					<Container padding={{ top: ContainerPadding.dynamic0020 }}>
						<Text
							appearance={TokenTextAppearance.copy200}
							staticSize
						>
							Elige un máximo de 3 opciones
						</Text>
					</Container>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<MultipleOptionCTAs
							ignoreHandleClick={true}
							ctaValues={CTAsMotivoRenovador}
							maxSelected={3}
							name="selectMotivos"
						/>
					}
				/>
			),
			screenIndex: index++,
			name: 'selectMotivos',
			ctas: CTAsMotivoRenovador,
		},
		{
			title: (
				<>
					<Text bold>
						¿Nos puedes decir cuáles{' '}
						<span style={{ whiteSpace: 'nowrap' }}>
							son?
							<sup style={{ top: 'auto', fontSize: '100%' }}>
								*
							</sup>
						</span>
					</Text>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<DetalleMotivo
							CTALabel="No, eso es todo"
							name="detalleMotivoRenovar"
						/>
					}
				/>
			),
			screenIndex: index++,
			name: 'detalleMotivoRenovar',
		},
		{
			title: (
				<>
					<Text bold>¿Por qué motivos </Text>
					tuviste problemas con{' '}
					<span style={{ whiteSpace: 'nowrap' }}>
						tu vehículo anterior?
						<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
					</span>
					<Container padding={{ top: ContainerPadding.dynamic0020 }}>
						<Text
							appearance={TokenTextAppearance.copy200}
							staticSize
						>
							Elige un máximo de 3 opciones
						</Text>
					</Container>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<MultipleOptionCTAs
							ignoreHandleClick={true}
							ctaValues={CTAsMotivoRenovadorProblemas}
							maxSelected={3}
							name="selectProblemas"
						/>
					}
				/>
			),
			screenIndex: index++,
			name: 'selectProblemas',
			ctas: CTAsMotivoRenovadorProblemas,
		},
		{
			fields: <ConfirmacionNoRenovacion idPerson={idPerson} />,
			screenIndex: index++,
			name: 'Confirmacion',
		},
	];

	useEffect(() => {
		if (brand) {
			updateSteps('lugarCompra', brand == vwBrandCode, 'usadoNuevo');
			updateSteps('selectMotivos', brand != vwBrandCode, 'usadoNuevo');
			updateSteps(
				'selectProblemas',
				brand != vwBrandCode,
				'selectMotivos',
			);
			updateSteps(
				'detalleMotivoRenovar',
				brand != vwBrandCode,
				'selectProblemas',
			);
		}
	}, [brand]);

	useEffect(() => {
		if (selectMotivos) handleMotivosRenovar();
	}, [selectMotivos]);

	const handleMotivosRenovar = () => {
		if (
			!(
				selectMotivos.includes(valueMotivoProblemas) &&
				selectMotivos.includes(valueMotivoOtros)
			)
		) {
			if (selectMotivos.includes(valueMotivoProblemas)) {
				updateSteps('detalleMotivoRenovar', false);
			} else if (selectMotivos.includes(valueMotivoOtros)) {
				updateSteps('selectProblemas', false);
			} else {
				updateSteps('detalleMotivoRenovar', false);
				updateSteps('selectProblemas', false);
			}
		}
	};

	const updateSteps = (
		stepName: string,
		conditional: boolean,
		addOnNextStepName?: string,
	) => {
		const { steps: actualSteps, screenIndex } = store.getState().formInfo;
		let newSteps = [...actualSteps];
		let newScreenIndex = screenIndex;

		if (conditional) {
			const stepIndex = getIndexInSteps(newSteps, stepName);

			if (stepIndex === -1) {
				if (addOnNextStepName) {
					newScreenIndex = getIndexInSteps(
						newSteps,
						addOnNextStepName,
					);
				}
				newSteps = addRemovedStep(
					steps,
					actualSteps,
					stepName,
					newScreenIndex,
				);
			}
		} else {
			newSteps = removeStep(newSteps, stepName);
		}

		const newStepsWithIndex = resetStepsScreenIndex(newSteps);

		dispatch({ type: 'UPDATE_STEPS', payload: newStepsWithIndex });
		dispatch({
			type: 'UPDATE_LAST_STEP',
			payload: newStepsWithIndex[newStepsWithIndex.length - 1],
		});
		dispatch({
			type: 'SET_NUMBER_OF_SCREENS',
			payload: newStepsWithIndex.length,
		});
	};

	useEffect(() => {
		if (!presubmitSent) sendForm(true);
	}, []);

	const NuevoFormData: Partial<FormDataGroup> = {
		fields: {
			formName: 'no_renovacion-ya_tengo',
			formType: 'otherform',
			pageCategory: 'Encuesta',
		}, // Analítica
	};

	const getFormInfo = (): {
		firstStep: Steps;
		lastStep: Steps;
		stepsHistory: number[];
		numberOfScreens: number;
		newSteps?: Steps[];
	} => {
		return {
			firstStep: steps[0],
			lastStep: steps[steps.length - 1],
			stepsHistory: [0],
			numberOfScreens: steps.length,
			newSteps: null,
		};
	};

	const generateData = (
		isPresubmit: boolean,
		data?: Partial<FormDataGroup>,
	) => {
		const { formData }: OneFormState = store.getState();
		const { fields } = formData;

		let motivos = fields.selectMotivos;

		if (fields.selectProblemas) {
			motivos = motivos.replace(
				valueMotivoProblemas,
				fields.selectProblemas,
			);
		} else {
			motivos = deleteProblema(motivos);
		}

		const submitData = !isPresubmit
			? {
					'fields[SURVEY_ANSWER_02]': fields.brand,
					'fields[SURVEY_ANSWER_03]': fields.modelo,
					'fields[SURVEY_ANSWER_04]': fields.usadoNuevo,
					'fields[SURVEY_ANSWER_05]': orderString(motivos),
					'fields[SURVEY_ANSWER_06]': fields.lugarCompra,
			  }
			: null;

		return {
			...getCommonFormData(
				isPresubmit,
				idPerson,
				idVehicle,
				dpsData,
				fields,
				data,
				'1',
			),
			...submitData,
		};
	};

	const orderString = orderMotivos => {
		if (orderMotivos == null) {
			return null;
		}

		const array = orderMotivos.split(',');

		const sortedArray = array.sort((a, b) => {
			return parseInt(a) - parseInt(b);
		});

		const orderString = sortedArray.join(',');

		return orderString;
	};

	const deleteProblema = motivosProblema => {
		if (motivosProblema != null) {
			const array = motivosProblema.split(',');

			if (
				array.length == 1 &&
				(array[0] == valueMotivoProblemas || array[0] == 'nada')
			) {
				return null;
			}

			const motivos = array.filter(
				numero => numero != valueMotivoProblemas,
			);

			return motivos.join(',');
		}
	};

	const sendForm = async (isPresubmit?: boolean): Promise<boolean> => {
		return new Promise<any>(async (resolve, reject) => {
			const data = generateData(isPresubmit, NuevoFormData);
			const dataStringified = JSON.stringify(data);
			const dataParsed = JSON.parse(dataStringified);
			const formData = formDataBuilder(dataParsed);
			const response = await oneformBFFNdpsSend(formData);

			if (response?.data?.status?.code == 200) {
				if (isPresubmit) setPresubmitSent(true);
				dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
				resolve(true);
			} else {
				if (isPresubmit) handleError();
				resolve(false);
			}
		});
	};

	const { firstStep, lastStep, loading } =
		useInitializeNoRenovacionForm(steps);

	useEffect(() => {
		const NuevoFormInfo: Partial<FormInfo> = {
			formStarted: true,
			sendFormCallback: sendForm,
			fullScreen: firstStep.fullScreen ? true : false,
			lastStep,
			numberOfScreens: steps.length,
			screenIndex: firstStep.screenIndex,
			formTheme: 'main',
			notALayer: true,
			formType: 'inPage',
			step: firstStep,
			steps,
			stepsHistory: [0],
			thankYouComponent: <ThankYouNoRenovacion />,
		};

		if (!loading) {
			initializeForm(NuevoFormInfo, NuevoFormData, true);
			trackFormLoad();
		}
	}, [loading]);

	return presubmitSent && steps ? (
		<FormController steps={steps} screenType="full-screen" />
	) : null;
}
